/**
 * Builds a complete assignment update payload for backend PUT requests.
 * 
 * The backend requires a full assignment object for updates, not partial payloads.
 * This helper provides a single source of truth for required fields and defensive field access.
 * 
 * @param {Object} assignment - The assignment object to build payload from
 * @param {Object} overrides - Fields to override in the final payload (e.g., { completed: false })
 * @returns {Object} Complete assignment payload ready for API submission
 */
export function buildAssignmentUpdatePayload(assignment, overrides = {}) {
  // Defensive field mapping - handle backend inconsistencies
  const id = assignment.assignmentId || assignment.id;

  return {
    // IDs
    assignmentId: assignment.assignmentId ?? id,
    id: assignment.id ?? id,

    // Core fields
    title: assignment.title ?? assignment.name ?? "",
    description: assignment.description ?? null,

    // Dates
    assignedDate: assignment.assignedDate ?? null,
    dueDatetime: assignment.dueDatetime || assignment.dueDate || null,

    // Relationships
    endeavorId: assignment.endeavorId || assignment.endeavorID || assignment.endeavor_id || null,
    eventId: assignment.eventId || assignment.eventID || assignment.event_id || null,

    // Status
    priority: assignment.priority || null,
    completed: assignment.completed ?? assignment.isDone ?? false,
    completedDatetime: assignment.completedDatetime ?? null,

    // Timestamps
    createdAt: assignment.createdAt ?? null,
    updatedAt: assignment.updatedAt ?? null,

    // Apply overrides last (e.g., { completed: false, completedDatetime: null })
    ...overrides,
  };
}
